import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow, userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import JudgeDrawer from "../components/JudgeDrawer";
import CompositeJudgeMapPanel from "../components/CompositeJudgeMapPanel";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useSortedRows } from "../hooks/useSortedRows";

const TAB_ROSTER = "roster";
const TAB_COMPOSITE = "composite";

// #732 — Judges section: list of the active show's judges (score-api
// GET /judge?showId=, seeded onto the daysheet by foxbox #729 and reconciled
// into score-judge by #730), with a per-judge drawer for signature capture.
// Same list+drawer pattern as HorseManager/PeopleManager (#507) minus search
// (shows have a small handful of judges, not hundreds).
export default function JudgeManager() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.judges"));
  const scoreApi = useScoreApi();
  // #2195 — `loading` (aliased loadingShow) covers the show-LIST fetch: while
  // it's still in flight activeShowId is null too, same shape as everywhere
  // else in scoreadmin-fe, and rendering "no show" text off a null id that
  // simply hasn't resolved yet told the operator there was no show before we
  // knew that.
  const { activeShowId, loading: loadingShow } = useActiveShow();

  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  // #1742 — new tab container. The roster table (below) stays the first tab,
  // unchanged; "Composite judges" is the new panel that maps one packed
  // roster row to its two real judges (card #1342's mapping entity).
  const [activeTab, setActiveTab] = useState(TAB_ROSTER);

  const loadJudges = useCallback(async () => {
    if (!activeShowId) {
      setJudges([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const list = unwrap(await scoreApi.judges.listJudges(activeShowId));
      setJudges(list);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [scoreApi, activeShowId]);

  useEffect(() => {
    loadJudges();
  }, [loadJudges]);

  const selected = judges.find((j) => j.id === selectedId) || null;

  // #870 — sortable columns, same pattern as HorseManager/PeopleManager (#557).
  const { sorted, sortKey, sortDir, toggleSort } = useSortedRows(
    judges,
    {
      name: (j) => userDisplayName(j),
      role: (j) => j.role,
      country: (j) => j.country,
    },
    "name",
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("judges.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("judges.loadError")}
        </Alert>
      )}

      {/* #1742 — textColor="inherit" + indicatorColor="secondary", same fix as
          RideEditor's judge tabs (#581/#587): the default selected-tab color
          is theme primary.main (brand darkGrey #363636), ~1.5:1 contrast on a
          dark background.paper and effectively unreadable. */}
      <Tabs
        value={activeTab}
        onChange={(_e, value) => setActiveTab(value)}
        textColor="inherit"
        indicatorColor="secondary"
        sx={{ mb: 2 }}
      >
        <Tab value={TAB_ROSTER} label={t("judges.tabs.roster")} />
        <Tab value={TAB_COMPOSITE} label={t("judges.tabs.composite")} />
      </Tabs>

      {activeTab === TAB_ROSTER ? (
        !activeShowId ? (
          loadingShow ? (
            <TableSkeleton rows={4} columns={4} />
          ) : (
            <Typography color="text.secondary">{t("judges.noShow")}</Typography>
          )
        ) : loading ? (
          <TableSkeleton rows={4} columns={4} />
        ) : judges.length === 0 ? (
          <Typography color="text.secondary">{t("judges.empty")}</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    { key: "name", labelKey: "judges.columns.name" },
                    { key: "role", labelKey: "judges.columns.role" },
                    { key: "country", labelKey: "judges.columns.country" },
                  ].map((col) => (
                    <TableCell key={col.key} sortDirection={sortKey === col.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortKey === col.key}
                        direction={sortKey === col.key ? sortDir : "asc"}
                        onClick={() => toggleSort(col.key)}
                      >
                        {t(col.labelKey)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell>{t("judges.columns.signature")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((judge) => (
                  <TableRow
                    key={judge.id}
                    hover
                    onClick={() => setSelectedId(judge.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{userDisplayName(judge) || "-"}</TableCell>
                    <TableCell>{judge.role || "-"}</TableCell>
                    <TableCell>{judge.country || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          judge.signaturePresent
                            ? t("judges.drawer.signatureOnFile")
                            : t("judges.drawer.signatureMissing")
                        }
                        color={judge.signaturePresent ? "success" : "default"}
                        variant={judge.signaturePresent ? "filled" : "outlined"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : (
        <CompositeJudgeMapPanel judges={judges} activeShowId={activeShowId} />
      )}

      <JudgeDrawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        judge={selected}
        onSignatureChanged={loadJudges}
      />
    </Box>
  );
}
